import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TriggersCreatedPageContainer,
  Content,
  TableContainer,
  NameWithVisibilityContainer,
  UnderlinedText,
  NodesContainer,
  CircularContainer,
  DropdownWrapper
} from "./TriggersCreatedPage.styled";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import Header from "../../components/Header";
import FilterBar from "../../components/FilterBar/FilterBar";
import Footer from "../../components/Footer";
import { Table, CardViewX, Dropdown, Alert, Tag, Typography, Tooltip} from "@ann/web-components";
import Icon from "../../../../../elements/icon/Icon";
import { EllipsisText } from "../../../../../elements";
import ShareModal from "../../components/ShareModal";
import ArchiveModal from "../../components/ArchiveModal";
import DuplicateModal from "../../components/DuplicateModal";
import PreviewSlideOut from "../../components/PreviewSlideOut";
import { useTriggers } from "../../context/TriggersContext";
import { useAlert } from "../../hooks/useAlert";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { toTitleCase } from '../../../../../utils/uiUtils';
import CircularProgress from '@mui/material/CircularProgress';
import { useStore } from "../../../../../hooks";
import { observer } from "mobx-react";


const getStatusConfig = (status) => {
  const statusMap = {
    'not_set_up_yet': { type: 'neutral', label: 'Not set up yet' },
    'active': { type: 'success', label: 'Active' },
    'paused': { type: 'alert', label: 'Paused' },
    'scheduled': { type: 'informative', label: 'Scheduled' },
    'auto_deactivated': { type: 'neutral', label: 'Auto deactivated' },
    'disabled': { type: 'neutral', label: 'Disabled' },
    'archived': { type: 'neutral', label: 'Archived' },
    'disabled_system': { type: 'error', label: 'Disabled (System)' },
    'DRAFT': { type: 'error', label: 'Draft' }
  };

  return statusMap[status] || { type: 'neutral', label: 'Unknown' };
};

const NameWithVisibility = ({ name, onClick, onClickName }) => (
  <NameWithVisibilityContainer onClick={onClick}>
    <Tooltip content="Preview">
      <Icon name="visibility" width="20px" height="20px" className="gray-icon" />
    </Tooltip>
    <UnderlinedText onClick={(e) => {e.stopPropagation(); onClickName && onClickName(e);}}>
      {name}
    </UnderlinedText>
  </NameWithVisibilityContainer>
);

const TriggersCreatedPage = observer(() => {
  const navigate = useNavigate();
  const { alert, showAlert, hideAlert } = useAlert();
  const { authStore } = useStore();

  const {
    filteredTriggers,
    loading,
    pagination,
    mode,
    loadTriggers,
    duplicateTriggerAction,
    archiveTriggerAction,
    setMode,
    setPage,
    setItemsPerPage
  } = useTriggers();


  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [triggerToArchive, setTriggerToArchive] = useState(null);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [triggerToDuplicate, setTriggerToDuplicate] = useState(null);
  const [selectedTriggerForPreview, setSelectedTriggerForPreview] = useState(null);
  dayjs.extend(relativeTime);

  useEffect(() => {
    loadTriggers();
  }, [loadTriggers]);

  const getMenuItems = (rowData) => [
    {
      id: 1,
      label: "Duplicate",
      onClick: () => {
        setTriggerToDuplicate(rowData);
        setIsDuplicateModalOpen(true);
      },
    },
    {
      id: 2,
      label: "Archive",
      onClick: () => {
        setTriggerToArchive(rowData);
        setIsArchiveModalOpen(true);
      },
    },
  ];

  const headers = [
    { value: "name", label: "Name" },
    { value: "type", label: "Type" },
    { value: "description", label: "Description" },
    { value: "createdBy", label: "Created By" },
    { value: "lastUpdated", label: "Last Updated" },
    { value: "executions", label: "Executions" },
    { value: "source", label: "Source" },
    { value: "nodes", label: "Nodes" },
    { value: "status", label: "Status" },
  ];

  const handleArchiveTrigger = async () => {
    if (triggerToArchive) {
      const result = await archiveTriggerAction(triggerToArchive);
      showAlert({
        title: result.success ? "Your Workflow was archived successfully" : "Error archiving workflow",
        message: result.success ? "You can restore it from your archive anytime." : "Please try again later.",
        severity: result.success ? "success" : "error",
      });
    }
  };

  const handleDuplicateTrigger = async () => {
    if (triggerToDuplicate) {
      const result = await duplicateTriggerAction(triggerToDuplicate, authStore.currentWorkspaceUuid);
      showAlert({
        title: result.success ? "Your Workflow has been successfully duplicated" : "Error duplicating workflow",
        message: result.success ? "You can modify if you wish." : "Please try again later.",
        severity: result.success ? "success" : "error",
      });
    }
  };


  const handleNameClick = (triggerData) => {
    setSelectedTriggerForPreview(triggerData);
    setSelectedTrigger(triggerData);
    setIsSlideoutOpen(true);
  };

  const getEnrolledCount = (enrolledText) => {
    if (!enrolledText || enrolledText === "No enrollments") return "0";
    const count = enrolledText.split(',').length;
    return `${count}`;
  };

  const tableData = filteredTriggers.map(trigger => ({
    ...trigger,
    uuid: trigger.uuid,
    nameText: trigger.name || trigger.nameText,
    name: (
      <NameWithVisibility
        name={trigger.name || trigger.nameText}
        onClick={() => handleNameClick(trigger)}
        onClickName={() => {
          localStorage.setItem('currentTriggerName', trigger.name || trigger.nameText);
          navigate(`/v2/workflows/${trigger.uuid}`, {
            state: { triggerName: trigger.name || trigger.nameText },
          });
        }}
      />
    ),
    type: toTitleCase(trigger.workflowType) || "Unknown",
    description: <EllipsisText text={trigger.description || trigger.descriptionText || "No workflow description available"} maxLength={30} />,
    descriptionText: trigger.description || trigger.descriptionText || "No workflow description available",
    createdBy: trigger.createdBy || "Unknown",
    lastUpdated: dayjs(trigger.lastUpdated).fromNow() ||  dayjs(trigger.lastUpdated).fromNow(trigger.updatedAt) || "Unknown",
    enrolled: <EllipsisText text={trigger.enrolled || "No enrollments"} maxLength={10} />,
    enrolledText: trigger.enrolled || "No enrollments",
    enrolledCount: getEnrolledCount(trigger.enrolled),
    source: trigger.source || "Unknown",
    executions: trigger.executions || 0,
    nodes: <NodesContainer><Icon name="nodosWorkflow" width="32px" /> {trigger.nodes || 0}</NodesContainer>,
    nodesText: trigger.nodes || 0,
    status: <Tag
      text={getStatusConfig(trigger.status).label}
      color={getStatusConfig(trigger.status).type}
      icon="info"
    />,
    statusValue: trigger.status,
    element: (
      <DropdownWrapper>
        <Dropdown items={getMenuItems(trigger)} size="small" type="secondary" />
      </DropdownWrapper>
    ),
  }));

  return (
    <>
      {alert && (
        <Alert
          title={alert.title}
          description={alert.message}
          severity={alert.severity}
          width={"692px"}
          top={"61px"}
          right={"16px"}
          onClose={hideAlert}
        />
      )}
      <TriggersCreatedPageContainer>
        <Content>
          <PreviewSlideOut
            open={isSlideoutOpen}
            onClose={() => {
              setIsSlideoutOpen(false);
              setSelectedTriggerForPreview(null);
            }}
            trigger={selectedTriggerForPreview}
          />
          {/* <Breadcrumb /> */}
          <Header />

          <FilterBar mode={mode} setMode={setMode} />

          <TableContainer>
            {loading ? (
              <CircularContainer>
                <CircularProgress />
              </CircularContainer>
            ) : (
              <Table
                headers={headers}
                data={tableData}
                mode={mode}
                gridColumns={3}
                loading={loading}
                gridNode={(item) => (
                  <CardViewX size="medium">
                    <CardViewX.Header
                      menu={
                        <DropdownWrapper>
                          <Dropdown
                            items={getMenuItems(item)}
                            size="small"
                            type="secondary"
                          />
                        </DropdownWrapper>
                      }
                      tag={{
                        label: getStatusConfig(item.statusValue).label,
                        type: getStatusConfig(item.statusValue).type,
                        icon: "info",
                      }}
                      title={item.nameText}
                    />
                    <Typography category="subtitle" size="medium" weight="bold">
                      {item.type}
                    </Typography>
                    <CardViewX.Description>
                      {item.descriptionText}
                    </CardViewX.Description>
                    {
                      /*
                      <CardViewX.Meta>
                        <CardViewX.MetaItem icon="person" label={item.createdBy} />
                        <CardViewX.MetaItem
                          icon="lastUpdated"
                          label={item.lastUpdated}
                        />
                        <CardViewX.MetaItem label={item.nodesText}>
                          <Icon name="nodosWorkflow" width="32px" />
                        </CardViewX.MetaItem>

                        <CardViewX.MetaItem icon="bolt" label={item.executions} />
                     </CardViewX.Meta>
                     */
                    }

                    <CardViewX.Actions>
                      <CardViewX.SecondaryAction
                        label="Preview"
                        onClick={() => handleNameClick(item)}

                      />

                      <CardViewX.PrimaryAction
                        label="Go to workflow"
                        onClick={() => {
                          localStorage.setItem('currentTriggerName', item.nameText);
                          navigate(`/v2/workflows/${item.uuid}`, {
                            state: { triggerName: item.nameText },
                          });
                        }}

                      />
                    </CardViewX.Actions>
                  </CardViewX>
                )}
              />
            )}
          </TableContainer>
        </Content>
      </TriggersCreatedPageContainer>

      {!loading && (
        <Footer
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          itemsPerPage={pagination.itemsPerPage}
          totalItems={pagination.totalItems}
          onPageChange={setPage}
          disabled={loading}
          onItemsPerPageChange={setItemsPerPage}
        />
      )}

      {selectedTrigger && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setSelectedTrigger(null);
          }}
          vaultId={selectedTrigger.uuid}
          vaultName={selectedTrigger.name}
        />
      )}
      {triggerToArchive && (
        <ArchiveModal
          isOpen={isArchiveModalOpen}
          onClose={() => {
            setIsArchiveModalOpen(false);
            setTriggerToArchive(null);
          }}
          onArchive={handleArchiveTrigger}
          triggerName={triggerToArchive.nameText}
        />
      )}
      {triggerToDuplicate && (
        <DuplicateModal
          isOpen={isDuplicateModalOpen}
          onClose={() => {
            setIsDuplicateModalOpen(false);
            setTriggerToDuplicate(null);
          }}
          onDuplicate={handleDuplicateTrigger}
          triggerName={triggerToDuplicate.nameText}
        />
      )}
    </>
  );
});
export default TriggersCreatedPage;
